import { ICH } from "./daten";
import type { Einsatzlage, Verlaufsart, Verlaufszeile, Wer } from "../einsatzlage";
import type { Beteiligter } from "../vorbereitung";
import type { HandyTag, HandyZeile } from "../typen";
import * as AUF from "./aufnahmen";

/* ══════════════════════════════════════════════════════════════════
   Die Einsätze des Prototyps — 18.09.2026.

   **Warum eine eigene Datei neben `daten.ts`.** Dieselbe Begründung wie bei
   [`aufnahmen.ts`](aufnahmen.ts): Ein Einsatz wird nicht nur gelesen,
   sondern **verändert** — genommen, gestempelt, vorzeitig abgeschlossen.
   Und er wird von zwei Seiten gelesen: die Tagesmappe holt ihre vier Listen
   (`routen.ts`, `handyHeute`), der Einsatzschirm seine Lage und seinen
   Verlauf (`daten/einsatzlage.ts`). **Beide müssen dieselbe Belegung
   meinen** — sonst tippt man in der Mappe auf eine Zeile und landet auf
   „Diesen Einsatz gibt es nicht mehr".

   `daten.ts` bleibt, was es ist: reine Beispieldaten ohne Verhalten. Hier
   stehen die Lagen **und** der Stand, der sich über eine Sitzung ändert.

   ── Warum es die Datei überhaupt gibt ─────────────────────────────

   Bis heute warf `daten/einsatzlage.ts` unter `VITE_ATTRAPPE=1` bei **jedem**
   Einsatz `404`, und `verlauf()` gab eine leere Liste zurück. Auf jedem
   Einsatzschirm stand „Diesen Einsatz gibt es nicht mehr" — ausgerechnet auf
   dem Schirm, der am 18.09.2026 in die Designsprache umgebaut wurde. **Lisa
   geht nach [`docs/29-pruefauftrag.md`](../../../../../docs/29-pruefauftrag.md)
   durch den Prototyp** und bekam ihn nie zu sehen.

   ── Acht Lagen, weil der Schirm acht Gesichter hat ────────────────

   Die Kurzfassung (`kurzsaetze()` in `masken/handy/Einsatz.tsx`) kennt neun
   Fälle, die Hauptaktion (`handlungen()`) ein Dutzend. Eine einzige
   Beispiellage zeigte davon eines. Deshalb liegt hier je ein Muster für die
   Lagen, die man beim Durchgehen wirklich sehen will:

   | Belegung | was man sieht |
   |---|---|
   | `be-rl07` | „Geht hinaus morgen 07:00" · Vorbereitung steht aus · der harte Riegel |
   | `be-kr01` | Arbeit läuft · „Seit … auf der Baustelle" · Pause, Tag abschließen |
   | `be-tr03` | Pause · „Tag 3 von 4 … heute 4,2 h erfasst" · „Einsatz abschließen" |
   | `be-rl11` | Mietgerät beim Kunden · Rücknahme offen |
   | `be-ms11` | **fremder Einsatz** · das Hindernisband „T. Brand ist gerade eingestempelt" |
   | `be-rp02` | Rücknahme heute · das Mietgerät kommt zurück |
   | `be-rl21` | frei im Pool · „Übernehmen" ändert den Stand der Sitzung |
   | `be-rl08` | abgeschlossen · von hier führt der Weg in die Zusammenfassung |

   > **⚠ Alle Zahlen sind Beispielwerte.** Zählerstände, Stundenzahlen und
   > Uhrzeiten sind erfunden; **ein Preis steht hier nirgends**. Was eine
   > Stunde kostet, sagt die Kalkulation aus `04-preisfindung.md` und nicht
   > der Prototyp (CLAUDE.md, „Was ohne Rückfrage nicht gesetzt werden darf").

   ── Die Zeit ist relativ, nicht fest ──────────────────────────────

   Wie in `aufnahmen.ts`: Ein festes Datum wäre nach einer Woche „Tag −4 von
   3". Gerechnet wird ab der Uhr des Geräts, damit „morgen 07:00" morgen
   immer noch morgen heißt.
   ══════════════════════════════════════════════════════════════════ */

/* ══════════════ Menschen und Zeit ══════════════ */

/** Der angemeldete Mensch — dieselbe Kennung wie in `daten.ts`. */
const MEINS: Wer = { id: ICH.id, name: ICH.name };
/** Der Kollege, dem ein Einsatz gehört, damit es einen fremden gibt. */
const BRAND: Wer = { id: "u-002", name: "T. Brand" };

const zz = (n: number): string => String(n).padStart(2, "0");

/** `2026-09-19` in der Uhr des Geräts — **örtlich**, nicht in UTC. */
function tagStempel(versatz = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + versatz);
  return `${d.getFullYear()}-${zz(d.getMonth() + 1)}-${zz(d.getDate())}`;
}

/** Ein Zeitpunkt an einem Tag relativ zu heute — `tagUm(1, 7)` ist morgen 07:00. */
function tagUm(versatz: number, stunde: number, minute = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + versatz);
  d.setHours(stunde, minute, 0, 0);
  return d.toISOString();
}

/** So viele Minuten zurück — für alles, was „gerade läuft". */
function vorMinuten(minuten: number): string {
  return new Date(Date.now() - minuten * 60_000).toISOString();
}

/** `07:00` aus einem Zeitpunkt — dieselbe kurze Form, die `alsHandyZeile()` baut. */
function uhr(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : `${zz(d.getHours())}:${zz(d.getMinutes())}`;
}

/** Eine Zeile des Verlaufs, wie sie `GET /handy/verlauf` liefert. */
function vz(
  art: Verlaufsart,
  am: string,
  wer: string,
  belegung: string,
  daten: Record<string, unknown> = {},
): Verlaufszeile {
  return { art, am, wer, belegung, daten, wartet: false, anzahl: 1 };
}

/* ══════════════ Die Form eines Musters ══════════════ */

/** In welche der vier Listen von `GET /handy/heute` die Zeile gehört. */
type Fach = "vorbereiten" | "raus" | "rein" | "laufend";

/**
 * Was die Zeile der Tagesmappe über die Lage hinaus trägt — Ort, Zufahrt,
 * Telefon, die geplanten Zeitpunkte. **Der Einsatzschirm liest sie mit**
 * (`zeileFinden()` in `masken/handy/Einsatz.tsx`): Woher „morgen 07:00" im
 * ersten Satz kommt, ist genau diese Zeile.
 */
type Mappe = {
  ressourceId: string;
  ort: string;
  ortschaft: string;
  anschrift: string;
  telefon: string;
  zufahrt: string;
  hinweis: string;
  vonIso: string;
  bisIso: string;
  /** 0 heute, 1 morgen — nur an der Vorbereitung. */
  tage: number | null;
  meins: boolean;
  darfNehmen: boolean;
  darfZurueckgeben: boolean;
  bediener: Beteiligter[];
  genommen: Beteiligter | null;
  /** Der letzte bekannte Zählerstand als Vorschlag im Feld. Beispielwert. */
  stundenVorschlag: string;
  /** Bei der Rücknahme: der Stand bei der Ausgabe. Beispielwert. */
  stundenAusgabe: string;
  ueberfaellig: boolean;
};

type Muster = { fach: Fach; lage: Einsatzlage; mappe: Mappe; verlauf: Verlaufszeile[] };

/** Was an einer Mappenzeile fast immer gleich ist. */
const MAPPE: Mappe = {
  ressourceId: "",
  ort: "",
  ortschaft: "",
  anschrift: "",
  telefon: "",
  zufahrt: "",
  hinweis: "",
  vonIso: "",
  bisIso: "",
  tage: null,
  meins: true,
  darfNehmen: false,
  darfZurueckgeben: false,
  bediener: [],
  genommen: null,
  stundenVorschlag: "",
  stundenAusgabe: "",
  ueberfaellig: false,
};

/** Was an einer Lage fast immer gleich ist — der Rest steht je Muster. */
const LAGE: Einsatzlage = {
  belegungId: "",
  eigeneBelegungId: null,
  ichFahre: false,
  heuteStempelbar: true,
  hindernis: null,
  kennung: "",
  typ: "",
  vorgang: "",
  kunde: "",
  zustand: "offen",
  mitBediener: false,
  unterschriftNoetig: true,
  stueckGeplant: null,
  uebergabeNoetig: true,
  tag: { nr: null, gesamt: 1, datum: null, bis: null },
  phase: null,
  einsatztag: null,
  letzterZaehler: null,
  vorbereitung: { erledigt: false, genommen: null },
  uebergabe: { erledigt: false, genommen: null },
  ruecknahme: { erledigt: false, genommen: null },
};

/* ══════════════════════════════════════════════════════════════════
   Die acht Muster
   ══════════════════════════════════════════════════════════════════ */

export const MUSTER: Muster[] = [
  /* 1 · **Geht morgen hinaus, Vorbereitung steht aus.** Das ist der Schirm,
     an dem der harte Riegel vom 18.09.2026 zu sehen ist: Die Hauptaktion
     heißt „Vorbereiten", daneben steht nur „Zurück in den Pool" — kein Weg
     an der Vorbereitung vorbei. Ein reines Mietgerät, also mit
     Übergabeprotokoll und Unterschrift. */
  {
    fach: "vorbereiten",
    lage: {
      ...LAGE,
      belegungId: "be-rl07",
      kennung: "RL-07",
      typ: "Radlader 5 t",
      vorgang: "V-2026-0142",
      kunde: "Bauer GmbH",
      zustand: "vorbereiten",
      tag: { nr: null, gesamt: 3, datum: null, bis: tagStempel(3) },
      // Beispielwert: der Stand aus dem letzten Protokoll dieser Maschine.
      letzterZaehler: { stand: 2140, quelle: "protokoll_ruecknahme", am: tagUm(-9, 16, 20) },
      vorbereitung: { erledigt: false, genommen: MEINS },
      uebergabe: { erledigt: false, genommen: MEINS },
    },
    mappe: {
      ...MAPPE,
      ressourceId: "RL-07",
      ort: "Baustelle Nordring",
      ortschaft: "Cloppenburg",
      anschrift: "Nordring 14, 49661 Cloppenburg",
      telefon: "+49 4471 123456",
      zufahrt: "Einfahrt an der Nordseite, Schranke mit Bauleiterschlüssel.",
      hinweis: "Bauleiter ist ab 06:45 vor Ort.",
      vonIso: tagUm(1, 7, 0),
      bisIso: tagUm(3, 16, 0),
      tage: 1,
      darfZurueckgeben: true,
      genommen: MEINS,
      stundenVorschlag: "2140,0",
    },
    verlauf: [
      /* „Zuletzt" darf nicht in der Zukunft stehen: Wer den Prototyp morgens
         öffnet, läse sonst „Zuletzt: 16:40". */
      vz("genommen", vorMinuten(95), MEINS.name, "be-rl07", { aufgabe: "uebergabe" }),
    ],
  },

  /* 2 · **Der Einsatz mit Fahrer, an dem gerade gearbeitet wird.** Die Uhr
     läuft, die Kurzfassung sagt „Seit … auf der Baustelle", die Hauptaktion
     ist „Tag abschließen", daneben „Pause". Von hier führt der Weg in den
     Tagesabschluss. */
  {
    fach: "laufend",
    lage: {
      ...LAGE,
      belegungId: "be-kr01",
      eigeneBelegungId: "be-kr01-ich",
      ichFahre: true,
      kennung: "KR-01",
      typ: "Autokran 30 t",
      vorgang: "V-2026-0151",
      kunde: "Solar Nord GmbH",
      zustand: "vor_ort",
      mitBediener: true,
      unterschriftNoetig: false,
      tag: { nr: 2, gesamt: 3, datum: tagStempel(0), bis: tagStempel(1) },
      phase: {
        eintragId: "zt-kr01-2", art: "arbeit", seit: vorMinuten(72),
        person: MEINS.name, meins: true, wartet: false, wartendId: null,
      },
      einsatztag: {
        datum: tagStempel(0),
        /* Beispielwert, und er **rechnet sich aus dem Verlauf**: von der
           Ankunft (−258 min) bis zur Pause (−102 min) sind 156 Minuten. Was
           seitdem läuft, zählt die Uhr dazu. Eine Zahl, die dazu nicht passt,
           fiele beim Durchgehen sofort auf. */
        sekunden: 9360,
        eintraege: 2,
        zaehlerBeginn: 1292,
        zaehlerEnde: null,
        bestaetigtAm: null,
        bestaetigtVon: "",
        offlineBestaetigt: false,
        ohneBestaetigungGrund: null,
      },
      letzterZaehler: { stand: 1292, quelle: "einsatztag_beginn", am: vorMinuten(266) },
      vorbereitung: { erledigt: true, genommen: MEINS },
      uebergabe: { erledigt: true, genommen: MEINS },
    },
    mappe: {
      ...MAPPE,
      ressourceId: "KR-01",
      ort: "Solarpark Nord, Feld B",
      ortschaft: "Garrel",
      anschrift: "Am Feld 3, 49681 Garrel",
      telefon: "+49 4474 887766",
      zufahrt: "Wirtschaftsweg ab der L 836, letzte 600 m geschottert.",
      hinweis: "Polier Klaassen quittiert die Stunden.",
      vonIso: tagUm(-1, 6, 30),
      bisIso: tagUm(1, 17, 0),
      bediener: [MEINS],
      stundenVorschlag: "1292,0",
    },
    /* **Der heutige Teil hängt an der Uhr, nicht am Kalender.** Stünde hier
       „losgefahren 06:12", zeigte der Schirm am Abend eine Anfahrt von
       vierzehn Stunden — nachgemessen im Browser, genau so war es. Die
       Abstände stimmen dafür immer: 52 Minuten Anfahrt, 2:36 h Arbeit, eine
       halbe Stunde Pause, und seit 1:12 h läuft es wieder. */
    verlauf: [
      vz("angekommen", vorMinuten(72), MEINS.name, "be-kr01-ich"),
      vz("beendet", vorMinuten(102), MEINS.name, "be-kr01-ich", { weiter_am: vorMinuten(72) }),
      vz("angekommen", vorMinuten(258), MEINS.name, "be-kr01-ich"),
      vz("zaehlerstand", vorMinuten(266), MEINS.name, "be-kr01-ich", { welcher: "beginn", stand: 1292 }),
      vz("losgefahren", vorMinuten(310), MEINS.name, "be-kr01-ich"),
      vz("bestaetigt", tagUm(-1, 17, 6), "", "be-kr01-ich", { von: "H. Klaassen", sekunden: 33_480 }),
      vz("uebergeben", tagUm(-1, 7, 25), MEINS.name, "be-kr01", { betriebsstunden: 1284.5, unterschrieben: true }),
      vz("vorbereitet", tagUm(-2, 15, 40), MEINS.name, "be-kr01", { mangel: false }),
    ],
  },

  /* 3 · **Der Einsatz in der Pause** — und der einzige, an dem sich „Einsatz
     abschließen" (B1) ausprobieren lässt: Er läuft, heute ist nicht der
     letzte Tag, und gerade läuft keine Zeit. Der Betrieb hat das
     Übergabeprotokoll an dieser Position **nicht** eingeschaltet (0126):
     Dann gibt es weder Übergabe noch Rücknahme zu erfassen, und der Tag
     endet mit der Bestätigung des Kunden. */
  {
    fach: "laufend",
    lage: {
      ...LAGE,
      belegungId: "be-tr03",
      eigeneBelegungId: "be-tr03-ich",
      ichFahre: true,
      kennung: "TR-03",
      typ: "Trecker 120 PS",
      vorgang: "V-2026-0158",
      kunde: "Hempen Agrar",
      zustand: "tag_offen",
      mitBediener: true,
      unterschriftNoetig: false,
      uebergabeNoetig: false,
      tag: { nr: 3, gesamt: 4, datum: tagStempel(0), bis: tagStempel(1) },
      einsatztag: {
        datum: tagStempel(0),
        // Beispielwert: 4,2 h — dieselbe Zahl, die der erste Satz nennt.
        sekunden: 15_120,
        eintraege: 3,
        zaehlerBeginn: 806,
        zaehlerEnde: null,
        bestaetigtAm: null,
        bestaetigtVon: "",
        offlineBestaetigt: false,
        ohneBestaetigungGrund: null,
      },
      letzterZaehler: { stand: 806, quelle: "einsatztag_beginn", am: vorMinuten(295) },
      vorbereitung: { erledigt: true, genommen: MEINS },
    },
    mappe: {
      ...MAPPE,
      ressourceId: "TR-03",
      ort: "Schlag am Bruchweg",
      ortschaft: "Garrel",
      anschrift: "Bruchweg 8, 49681 Garrel",
      telefon: "+49 4474 445566",
      zufahrt: "Hofeinfahrt, dann links am Silo vorbei.",
      hinweis: "Mulcher MU-01 hängt am Trecker.",
      vonIso: tagUm(-2, 7, 0),
      bisIso: tagUm(1, 17, 0),
      bediener: [MEINS],
      stundenVorschlag: "806,0",
    },
    /* 252 Minuten zwischen Ankunft und Pause — dieselben 4,2 h, die der erste
       Satz nennt. Und 33 Minuten Anfahrt davor. */
    verlauf: [
      vz("beendet", vorMinuten(35), MEINS.name, "be-tr03-ich"),
      vz("angekommen", vorMinuten(287), MEINS.name, "be-tr03-ich"),
      vz("zaehlerstand", vorMinuten(295), MEINS.name, "be-tr03-ich", { welcher: "beginn", stand: 806 }),
      vz("losgefahren", vorMinuten(320), MEINS.name, "be-tr03-ich"),
      vz("bestaetigt", tagUm(-1, 16, 52), "", "be-tr03-ich", { von: "J. Hempen", sekunden: 30_600 }),
      vz("vorbereitet", tagUm(-2, 6, 40), MEINS.name, "be-tr03", { mangel: false }),
    ],
  },

  /* 4 · **Das Mietgerät beim Kunden.** Übergabe steht, die Rücknahme ist
     noch Tage hin: Die Kurzfassung sagt, wo die Maschine ist und wann sie
     zurückkommt, die Rücknahme steht als Nebenaktion daneben — sie darf
     jederzeit jemand an sich ziehen. */
  {
    fach: "laufend",
    lage: {
      ...LAGE,
      belegungId: "be-rl11",
      kennung: "RL-11",
      typ: "Radlader 5 t",
      vorgang: "V-2026-0142",
      kunde: "Bauer GmbH",
      zustand: "vor_ort",
      tag: { nr: 2, gesamt: 5, datum: tagStempel(0), bis: tagStempel(3) },
      letzterZaehler: { stand: 1284.5, quelle: "protokoll_ausgabe", am: tagUm(-1, 7, 25) },
      vorbereitung: { erledigt: true, genommen: BRAND },
      uebergabe: { erledigt: true, genommen: BRAND },
    },
    mappe: {
      ...MAPPE,
      ressourceId: "RL-11",
      ort: "Baustelle Nordring",
      ortschaft: "Cloppenburg",
      anschrift: "Nordring 14, 49661 Cloppenburg",
      telefon: "+49 4471 123456",
      zufahrt: "Einfahrt an der Nordseite, Schranke mit Bauleiterschlüssel.",
      vonIso: tagUm(-1, 7, 25),
      bisIso: tagUm(3, 16, 0),
      darfNehmen: true,
      stundenVorschlag: "1284,5",
      stundenAusgabe: "1284,5",
    },
    verlauf: [
      vz("uebergeben", tagUm(-1, 7, 25), BRAND.name, "be-rl11", { betriebsstunden: 1284.5, unterschrieben: true }),
      vz("foto", tagUm(-1, 7, 22), BRAND.name, "be-rl11", { kategorie: "uebersicht" }),
      vz("vorbereitet", tagUm(-2, 16, 10), BRAND.name, "be-rl11", { mangel: false }),
      vz("genommen", tagUm(-2, 15, 55), BRAND.name, "be-rl11", { aufgabe: "uebergabe" }),
    ],
  },

  /* 5 · **Der Einsatz, der jemand anderem gehört.** Er steht in der Mappe,
     weil die Disposition ihn sieht — zu tun ist hier aber nichts, und genau
     das muss dastehen: Das **Hindernisband** (18.09.2026) sagt „T. Brand ist
     gerade an diesem Einsatz eingestempelt". Ohne diese Lage wäre die
     wichtigste Auskunft des Schirms im Prototyp unsichtbar.

     Damit sie erscheint, muss wirklich **kein** Schritt übrig sein
     (`warumNichtsGeht`): kein Nebenknopf, als Hauptaktion nur „Zurück zu
     Heute". Deshalb ohne Übergabeprotokoll (0126) und nicht am letzten Tag. */
  {
    fach: "laufend",
    lage: {
      ...LAGE,
      belegungId: "be-ms11",
      kennung: "MS-11",
      typ: "Minibagger 1,8 t",
      vorgang: "V-2026-0147",
      kunde: "Meyer Bau",
      zustand: "vor_ort",
      mitBediener: true,
      unterschriftNoetig: false,
      uebergabeNoetig: false,
      hindernis: { grund: "laeuft_bei_anderem", wer: BRAND.name },
      tag: { nr: 2, gesamt: 3, datum: tagStempel(0), bis: tagStempel(1) },
      phase: {
        eintragId: "zt-ms11-1", art: "arbeit", seit: vorMinuten(145),
        person: BRAND.name, meins: false, wartet: false, wartendId: null,
      },
      einsatztag: {
        datum: tagStempel(0),
        /* Nichts Abgeschlossenes: Was T. Brand heute erfasst hat, läuft noch
           — die Uhr zählt es aus der Phase. */
        sekunden: 0,
        eintraege: 1,
        zaehlerBeginn: 431,
        zaehlerEnde: null,
        bestaetigtAm: null,
        bestaetigtVon: "",
        offlineBestaetigt: false,
        ohneBestaetigungGrund: null,
      },
      letzterZaehler: { stand: 431, quelle: "einsatztag_beginn", am: vorMinuten(150) },
      vorbereitung: { erledigt: true, genommen: BRAND },
    },
    mappe: {
      ...MAPPE,
      ressourceId: "MS-11",
      ort: "Baugebiet Südfeld",
      ortschaft: "Lohne",
      anschrift: "Südfeld 22, 49393 Lohne",
      telefon: "+49 4442 998877",
      vonIso: tagUm(-1, 7, 0),
      bisIso: tagUm(1, 17, 0),
      meins: false,
      bediener: [BRAND],
      stundenVorschlag: "431,0",
    },
    verlauf: [
      vz("angekommen", vorMinuten(145), BRAND.name, "be-ms11-brand"),
      vz("zaehlerstand", vorMinuten(150), BRAND.name, "be-ms11-brand", { welcher: "beginn", stand: 431 }),
      vz("losgefahren", vorMinuten(183), BRAND.name, "be-ms11-brand"),
      vz("bestaetigt", tagUm(-1, 16, 40), "", "be-ms11-brand", { von: "R. Meyer", sekunden: 30_600 }),
    ],
  },

  /* 6 · **Die Rücknahme von heute.** Das Mietgerät kommt zurück: Die
     Kurzfassung nennt die Uhrzeit, die Hauptaktion heißt „Rücknahme
     erfassen". Das ist die Zeile unter REIN. */
  {
    fach: "rein",
    lage: {
      ...LAGE,
      belegungId: "be-rp02",
      kennung: "RP-02",
      typ: "Rüttelplatte 500",
      vorgang: "V-2026-0139",
      kunde: "Schulz KG",
      zustand: "vor_ort",
      tag: { nr: 3, gesamt: 3, datum: tagStempel(0), bis: tagStempel(0) },
      letzterZaehler: { stand: 318, quelle: "protokoll_ausgabe", am: tagUm(-2, 8, 5) },
      vorbereitung: { erledigt: true, genommen: MEINS },
      uebergabe: { erledigt: true, genommen: MEINS },
      ruecknahme: { erledigt: false, genommen: MEINS },
    },
    mappe: {
      ...MAPPE,
      ressourceId: "RP-02",
      ort: "Kanalbaustelle Falkenweg",
      ortschaft: "Vechta",
      anschrift: "Falkenweg 5, 49377 Vechta",
      telefon: "+49 4441 556677",
      hinweis: "Platte steht im Container neben dem Bauwagen.",
      vonIso: tagUm(-2, 8, 5),
      bisIso: tagUm(0, 16, 0),
      genommen: MEINS,
      darfZurueckgeben: true,
      stundenVorschlag: "324,0",
      stundenAusgabe: "318,0",
    },
    verlauf: [
      vz("genommen", vorMinuten(150), MEINS.name, "be-rp02", { aufgabe: "ruecknahme" }),
      vz("uebergeben", tagUm(-2, 8, 5), MEINS.name, "be-rp02", { betriebsstunden: 318, unterschrieben: true }),
      vz("vorbereitet", tagUm(-3, 15, 20), MEINS.name, "be-rp02", { mangel: true }),
    ],
  },

  /* 7 · **Die freie Aufgabe im Pool.** Der einzige Einsatz, den noch niemand
     hat: Die Hauptaktion heißt „Übernehmen", und danach ändert sich der Stand
     wirklich (`einsatzUebernehmen`) — Zustand, Hauptaktion und Verlauf
     springen. Ohne ihn wäre in der Mappe der Abschnitt „Frei" leer und der
     Knopf, der die Sitzung verändert, nirgends zu drücken. */
  {
    fach: "raus",
    lage: {
      ...LAGE,
      belegungId: "be-rl21",
      kennung: "RL-21",
      typ: "Radlader 6 t",
      vorgang: "V-2026-0161",
      kunde: "Kruse GaLaBau",
      zustand: "offen",
      tag: { nr: null, gesamt: 3, datum: null, bis: tagStempel(2) },
      letzterZaehler: { stand: 964, quelle: "protokoll_ruecknahme", am: tagUm(-11, 15, 5) },
    },
    mappe: {
      ...MAPPE,
      ressourceId: "RL-21",
      ort: "Gartenbau Kruse, Hof",
      ortschaft: "Garrel",
      anschrift: "Hauptstraße 41, 49681 Garrel",
      telefon: "+49 4474 223344",
      hinweis: "Abholung durch den Kunden — Papiere bereitlegen.",
      vonIso: tagUm(0, 13, 0),
      bisIso: tagUm(2, 16, 0),
      tage: 0,
      meins: false,
      darfNehmen: true,
      stundenVorschlag: "964,0",
    },
    verlauf: [],
  },

  /* 8 · **Der abgeschlossene Einsatz** — der einzige Weg in die
     Zusammenfassung (`/handy/einsatz/:belegung/zusammenfassung`). Der Vorgang
     ist mit Absicht `V-2026-0155`: Zu ihm liegen in `daten.ts` beide
     Protokolle samt Schaden, sodass die Zusammenfassung nicht aus lauter
     Strichen besteht. */
  {
    fach: "rein",
    lage: {
      ...LAGE,
      belegungId: "be-rl08",
      kennung: "RL-08",
      typ: "Radlader 5 t",
      vorgang: "V-2026-0155",
      kunde: "Ahlers Bau & Tief",
      zustand: "abgeschlossen",
      tag: { nr: 3, gesamt: 3, datum: tagStempel(0), bis: tagStempel(0) },
      letzterZaehler: { stand: 1301, quelle: "protokoll_ruecknahme", am: vorMinuten(180) },
      vorbereitung: { erledigt: true, genommen: MEINS },
      uebergabe: { erledigt: true, genommen: MEINS },
      ruecknahme: { erledigt: true, genommen: MEINS },
    },
    mappe: {
      ...MAPPE,
      ressourceId: "RL-08",
      ort: "Erschließung Ost",
      ortschaft: "Emstek",
      anschrift: "Am Ostring 2, 49685 Emstek",
      telefon: "+49 4473 334455",
      vonIso: tagUm(-2, 7, 12),
      bisIso: vorMinuten(180),
      genommen: MEINS,
      stundenVorschlag: "1301,0",
      stundenAusgabe: "1284,5",
    },
    verlauf: [
      vz("zurueckgenommen", vorMinuten(180), MEINS.name, "be-rl08", {
        betriebsstunden: 1301, unterschrieben: true,
      }),
      vz("schaden", vorMinuten(182), MEINS.name, "be-rl08"),
      vz("foto", vorMinuten(184), MEINS.name, "be-rl08", { kategorie: "schaden" }),
      vz("uebergeben", tagUm(-2, 7, 12), MEINS.name, "be-rl08", {
        betriebsstunden: 1284.5, unterschrieben: true,
      }),
      vz("vorbereitet", tagUm(-3, 16, 30), MEINS.name, "be-rl08", { mangel: false }),
    ],
  },
];

/* ══════════════════════════════════════════════════════════════════
   Der Stand für die Dauer der Sitzung

   Derselbe Griff wie bei den Ortsaufnahmen (`aufnahmen.ts`) und den
   Belegungen (`routen.ts`): eine veränderliche Kopie, die lebt, solange der
   Reiter offen ist. **Ohne sie täte „Übernehmen" nichts** — der Schirm
   frischt danach seine Lage auf und bekäme dieselbe zurück. Genau das soll
   sich aber ausprobieren lassen.
   ══════════════════════════════════════════════════════════════════ */

const kopie = (m: Muster): Muster => ({
  ...m,
  lage: {
    ...m.lage,
    tag: { ...m.lage.tag },
    phase: m.lage.phase ? { ...m.lage.phase } : null,
    einsatztag: m.lage.einsatztag ? { ...m.lage.einsatztag } : null,
    letzterZaehler: m.lage.letzterZaehler ? { ...m.lage.letzterZaehler } : null,
    vorbereitung: { ...m.lage.vorbereitung },
    uebergabe: { ...m.lage.uebergabe },
    ruecknahme: { ...m.lage.ruecknahme },
  },
  mappe: { ...m.mappe },
  verlauf: m.verlauf.map((z) => ({ ...z })),
});

let stand: Muster[] = MUSTER.map(kopie);

/**
 * Ein Muster über die Belegung — **auch über die eigene Bediener-Belegung**.
 *
 * Die Adresse `/handy/einsatz/:belegung` darf beide nennen (so steht es im
 * Kopf von `masken/handy/Einsatz.tsx`), und wer aus der Zeiterfassung kommt,
 * hat die des Bedieners in der Hand.
 */
function finden(belegung: string): Muster | null {
  return stand.find((m) => m.lage.belegungId === belegung || m.lage.eigeneBelegungId === belegung) ?? null;
}

/** Die Lage eines Einsatzes. `null` heißt: Die Belegung gibt es nicht. */
export function eineLage(belegung: string): Einsatzlage | null {
  const m = finden(belegung);
  return m ? kopie(m).lage : null;
}

/** Der Verlauf dazu, neueste zuerst — wie `GET /handy/verlauf` ihn sortiert. */
export function verlaufVon(belegung: string): Verlaufszeile[] {
  const m = finden(belegung);
  if (!m) return [];
  return m.verlauf.map((z) => ({ ...z })).sort((a, b) => b.am.localeCompare(a.am));
}

/** Aus einem Muster die Zeile, die `GET /handy/heute` in seinen Listen führt. */
function alsZeile(m: Muster): HandyZeile {
  const { lage, mappe, fach } = m;
  // RAUS und die Vorbereitung lesen den Beginn, REIN und LÄUFT das Ende.
  const zeitpunkt = fach === "rein" || fach === "laufend" ? mappe.bisIso : mappe.vonIso;
  return {
    belegungId: lage.belegungId,
    ressourceId: mappe.ressourceId,
    kennung: lage.kennung,
    typ: lage.typ,
    kunde: lage.kunde,
    ort: mappe.ort,
    ortschaft: mappe.ortschaft,
    zufahrt: mappe.zufahrt,
    hinweis: mappe.hinweis,
    vorgang: lage.vorgang,
    zeit: uhr(zeitpunkt),
    ueberfaellig: mappe.ueberfaellig,
    stundenVorschlag: mappe.stundenVorschlag,
    stundenAusgabe: mappe.stundenAusgabe,
    tage: mappe.tage,
    vonIso: mappe.vonIso || null,
    genommen: mappe.genommen,
    meins: mappe.meins,
    darfNehmen: mappe.darfNehmen,
    darfZurueckgeben: mappe.darfZurueckgeben,
    bediener: mappe.bediener,
    bedienerBelegungId: lage.eigeneBelegungId,
    zeitQuittiertAm: lage.einsatztag?.bestaetigtAm ?? null,
    zeitErfasst: (lage.einsatztag?.eintraege ?? 0) > 0,
    zeitLaeuft: lage.phase !== null,
    bisIso: mappe.bisIso || null,
    anschrift: mappe.anschrift,
    telefon: mappe.telefon,
    unterschriftNoetig: lage.unterschriftNoetig,
  };
}

/**
 * Die vier Listen der Tagesmappe — **aus demselben Stand wie die Lagen**.
 *
 * Das ist der ganze Zweck der Datei: Wer in der Mappe auf eine Zeile tippt,
 * landet auf einem Einsatzschirm, den es gibt.
 */
export function handyTag(): HandyTag {
  const je = (fach: Fach): HandyZeile[] => stand.filter((m) => m.fach === fach).map(alsZeile);
  return {
    vorbereiten: je("vorbereiten"),
    raus: je("raus"),
    rein: je("rein"),
    laufend: je("laufend"),
    // Die offenen Ortsaufnahmen (19.09.2026) — aus dem Stand der Begehungen.
    aufnahmen: AUF.alsAufgaben(),
  };
}

function aendern(belegung: string, wie: (m: Muster) => Muster): void {
  const treffer = finden(belegung);
  if (!treffer) return;
  stand = stand.map((m) => (m === treffer ? wie(m) : m));
}

/**
 * Übernehmen — `POST /handy/uebernehmen`, und mit `ruecknahme` die Rücknahme
 * als eigene Aufgabe (`POST /aufgaben/nehmen`).
 *
 * Danach steht mein Name an der Aufgabe, der Zustand springt von „offen" auf
 * „vorbereiten", und die Hauptaktion wechselt. Der Verlauf bekommt seine
 * Zeile, damit „Zuletzt" unten am Schirm etwas zu sagen hat.
 */
export function einsatzUebernehmen(belegung: string, ruecknahme = false, grund: string | null = null): void {
  const jetzt = new Date().toISOString();
  const meins = { ...MEINS, am: jetzt };
  aendern(belegung, (m) => {
    const k = kopie(m);
    /* Hatte ihn ein anderer, ist es ein Wechsel (20.09.2026): Die Zeile im
       Verlauf nennt ihn und den Grund — wie `dienste/verlauf.ts` es aus dem
       Paar Rückgabe/Übernahme liest. */
    const vorher = ruecknahme ? k.lage.ruecknahme.genommen : k.lage.uebergabe.genommen ?? k.lage.vorbereitung.genommen;
    const wechsel = vorher && vorher.id !== MEINS.id ? { von: vorher.name, grund } : {};
    if (ruecknahme) k.lage.ruecknahme = { ...k.lage.ruecknahme, genommen: meins };
    else {
      k.lage.vorbereitung = { ...k.lage.vorbereitung, genommen: meins };
      k.lage.uebergabe = { ...k.lage.uebergabe, genommen: meins };
    }
    k.mappe = { ...k.mappe, genommen: MEINS, meins: true, darfNehmen: false, darfZurueckgeben: true };
    k.verlauf = [
      vz("genommen", jetzt, MEINS.name, k.lage.belegungId, {
        aufgabe: ruecknahme ? "ruecknahme" : "uebergabe", mit_bediener: k.lage.mitBediener, ...wechsel,
      }),
      ...k.verlauf,
    ];
    return k;
  });
}

/**
 * **An einen Kollegen weitergeben** — `POST /handy/uebergeben` (20.09.2026).
 * Danach steht sein Name an der Aufgabe, und der Verlauf sagt, von wem er sie
 * hat und warum.
 */
export function einsatzUebergeben(belegung: string, wer: Wer, grund: string | null = null): void {
  const jetzt = new Date().toISOString();
  const neu = { id: wer.id, name: wer.name, am: jetzt };
  aendern(belegung, (m) => {
    const k = kopie(m);
    const vorher = k.lage.uebergabe.erledigt ? k.lage.ruecknahme.genommen
      : k.lage.uebergabe.genommen ?? k.lage.vorbereitung.genommen;
    const wechsel = vorher ? { von: vorher.name, grund } : {};
    if (k.lage.uebergabe.erledigt) k.lage.ruecknahme = { ...k.lage.ruecknahme, genommen: neu };
    else {
      if (!k.lage.vorbereitung.erledigt) k.lage.vorbereitung = { ...k.lage.vorbereitung, genommen: neu };
      k.lage.uebergabe = { ...k.lage.uebergabe, genommen: neu };
    }
    k.mappe = { ...k.mappe, genommen: { id: wer.id, name: wer.name }, meins: false, darfNehmen: false, darfZurueckgeben: false };
    k.verlauf = [
      vz("genommen", jetzt, wer.name, k.lage.belegungId, {
        aufgabe: k.lage.uebergabe.erledigt ? "ruecknahme" : "uebergabe", mit_bediener: k.lage.mitBediener, ...wechsel,
      }),
      ...k.verlauf,
    ];
    return k;
  });
}

/** Wer den Einsatz an meiner Stelle nehmen könnte — im Prototyp der eine Kollege. */
export function kollegenVon(_belegung: string): Wer[] {
  return [BRAND];
}

/**
 * **Heute zum letzten Tag erklären** (B1) — `POST /handy/einsatz/abschliessen`.
 *
 * Gesetzt wird kein Ist und unterschrieben nichts: Der geplante Schluss rückt
 * auf heute. Danach ist heute der letzte Tag, und der gewohnte Weg läuft zu
 * Ende — daran ändert der Prototyp nichts.
 */
export function einsatzVorzeitigAbschliessen(belegung: string): void {
  aendern(belegung, (m) => {
    const k = kopie(m);
    const heute = tagStempel(0);
    k.lage.tag = { ...k.lage.tag, gesamt: k.lage.tag.nr ?? k.lage.tag.gesamt, bis: heute };
    k.mappe = { ...k.mappe, bisIso: tagUm(0, 17, 0) };
    return k;
  });
}
